'use client'

import { zodResolver } from '@hookform/resolvers/zod';
import cn from 'classnames';
import { useLocale, useTranslations } from 'next-intl';
import { FormEventHandler, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button, IconInput, PasswordInput } from '@components/Form';
import RadioGroup from '@components/Form/RadioGroup';
import Stepper from '@components/simple/Stepper';
import { newslettersByCountries } from '@utils/newsletter';


const defaultValues = {
  email: '',
  firstName: '',
  lastName: '',
  password: '',
};

const schema = z.object({
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  password: z.string().min(8),
  newsletter: z.string().optional(),
});

type SchemaType = z.infer<typeof schema>;

const PasswordChecker = ({ password, triedSubmitting }: { password: string, triedSubmitting: boolean }) => {
  const t = useTranslations('auth.registerV2.passwordChecker');
  const correctLength = /.{8,}/.test(password);
  const hasUpper = /[A-Z]/.test(password);
  const hasSpecialOrNumber = /[^A-Za-z]/.test(password);

  return <div className='flex flex-col items-center text-xs mt-2'>
    {(!correctLength || !hasUpper || !hasSpecialOrNumber) ? <div>{t('weakPassword')}</div> : <div>{t('strongPassword')}</div>}
    <div className='flex flex-col'>
      <div className={cn(correctLength ? 'text-green dark:text-white' : !triedSubmitting ? '' : 'text-red-500')}>
        {correctLength ? "✓" : "-"}{" "}
        {t('8Characters')}
      </div>
      <div className={cn(hasUpper ? 'text-green dark:text-white' : !triedSubmitting ? '' : 'text-red-500')}>
        {hasUpper ? "✓" : "-"}{" "}
        {t('upperCase')}
      </div>
      <div className={cn(hasSpecialOrNumber ? 'text-green dark:text-white' : !triedSubmitting ? '' : 'text-red-500')}>
        {hasSpecialOrNumber ? "✓" : "-"}{" "}
        {t('specialOrNumber')}
      </div>
    </div>
  </div>
}

const FinishRegistrationForm = ({ title, formDisabled, finishLabel, forceStep, onSubmit }: { title: string, formDisabled: boolean, finishLabel?: string, onSubmit?: (data: SchemaType) => Promise<void>, forceStep?: number }) => {
  const locale = useLocale();
  const t = useTranslations();
  const tRegister = useTranslations('auth.registerV2');
  const [step, setStep] = useState(forceStep || 1);
  const [triedToGoToNextStep, setTriedToGoToNextStep] = useState(false);
  const refinedDefaultValues = {
    ...defaultValues,
  }
  const currentStep = forceStep || step;
  finishLabel = finishLabel || t('main.label.buttons.save');

  const {
    watch,
    handleSubmit,
    register,
    formState: { errors, isSubmitting }
  } = useForm<SchemaType>({
    defaultValues: refinedDefaultValues,
    resolver: zodResolver(schema),
  });

  const password = watch('password');

  const checkPassword = () => {
    setTriedToGoToNextStep(true);
    if (password.length >= 8) {

      setStep(2);
    }
  }

  const newsletterOptions = newslettersByCountries[locale]
    .map((newsletter) => {
      return {
        label: `${t(
          `newsletter.workingOccupation.${newsletter}`,
        )}`,
        value: newsletter,
      };
    });

  const onFormSubmit: FormEventHandler = handleSubmit(async (data) => {
    if (onSubmit) {
      await onSubmit(data);
      return;
    }
  });
  return <>

    <form onSubmit={onFormSubmit}>
      <Stepper step={currentStep} steps={[tRegister('stepperLabels.password'), tRegister('stepperLabels.informations')]} />
      {currentStep === 1 && <>
        <h2 className='mb-8 mt-4 text-center text-lg font-bold lg:text-2xl'>{title}</h2>

        <div className='lg:px-12 md:px-8'>
          <PasswordInput
            disabled={isSubmitting || formDisabled}
            {...register('password')}
            error={!!errors.password}
            className="w-full px-4 text-sm font-light text-black bg-white rounded-lg"
            placeholder={tRegister('finishForm.password')}
          />
        </div>
        {(password || triedToGoToNextStep) && <PasswordChecker password={password} triedSubmitting={triedToGoToNextStep} />}
        {errors.password && (
          <p className="text-xs mb-4 text-red-500-500">{errors.password.message}</p>
        )}
        <div className='flex flex-col items-center mt-8'>
          <Button
            type="button"
            disabled={isSubmitting || formDisabled}
            onClick={checkPassword}
            className="w-[200px]"
          >
            {tRegister('finishForm.confirmPassword')}
          </Button>
        </div>
      </>}
      {currentStep === 2 && <>
        <h2 className='mb-2 mt-4 text-center text-lg font-bold lg:text-2xl'>
          {tRegister('finishForm.tellUsMore')}
        </h2>
        <h3 className='mb-2 mt-4 text-lg lg:text-lg'>
          {tRegister('finishForm.howShouldWeCallYou')}
        </h3>
        <div className='lg:px-4'>
          <IconInput
            icon='person'
            disabled={isSubmitting || formDisabled}
            containerClass='mb-4'
            type="text"
            {...register('firstName')}
            error={!!errors.firstName}
            className="w-full px-4 py-2text-sm font-light text-black bg-white rounded-lg"
            placeholder={tRegister('finishForm.firstName')}
          />
          {errors.firstName && (
            <p className="text-xs mb-4 text-red-500-500">{errors.firstName.message}</p>
          )}
          <IconInput
            icon='person'
            disabled={isSubmitting || formDisabled}
            type="text"
            {...register('lastName')}
            error={!!errors.lastName}
            containerClass='mb-4'
            className="w-full px-4 py-2 text-sm font-light text-black bg-white rounded-lg"
            placeholder={tRegister('finishForm.lastName')}
          />
          {errors.lastName && (
            <p className="text-xs mb-4 text-red-500-500">{errors.lastName.message}</p>
          )}
        </div>
        <h3 className='mb-2'>{t('newsletter.workingAs')}</h3>
        <div className='lg:px-4'>
          <RadioGroup
            name="newsletter"
            register={register}
            disabled={isSubmitting || formDisabled}
            error={!!errors.newsletter}
            elements={newsletterOptions}
          />
        </div>
        {errors.lastName && (
          <p className="text-xs mb-4 text-red-500-500">{errors.lastName.message}</p>
        )}
        <div className='flex flex-col items-center mt-4'>
          <Button
            type="submit"
            disabled={isSubmitting || formDisabled}
            loading={isSubmitting}
            className="w-[200px] mt-4"
          >
            {finishLabel}
          </Button>
        </div>
      </>}
    </form>
  </>
}

export { FinishRegistrationForm }